import { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import useSWR from 'swr';
import { apiErrorMessage } from '@/lib/api-client';

export type HomePage<TItem, TCursor> = {
  items: TItem[];
  has_more: boolean;
  nextCursor: TCursor;
};

/**
 * First page via SWR, then append with the cursor the endpoint returns.
 * Endpoint-specific request building stays in the caller.
 */
export function useHomePagedList<TItem, TCursor>({
  enabled = true,
  swrKey,
  initialCursor,
  fetchPage,
  loadMoreErrorMessage,
}: {
  enabled?: boolean;
  swrKey: readonly unknown[] | null;
  initialCursor: TCursor;
  fetchPage: (cursor: TCursor) => Promise<HomePage<TItem, TCursor>>;
  loadMoreErrorMessage: string;
}) {
  const [items, setItems] = useState<TItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const cursorRef = useRef(initialCursor);

  const { error, isLoading, mutate } = useSWR(
    enabled ? swrKey : null,
    async () => {
      const page = await fetchPage(initialCursor);
      cursorRef.current = page.nextCursor;
      setItems(page.items);
      setHasMore(page.has_more);
      return page;
    },
    {
      refreshWhenHidden: false,
      revalidateOnFocus: true,
    },
  );

  const loadMore = useCallback(async () => {
    if (!enabled || isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      const page = await fetchPage(cursorRef.current);
      cursorRef.current = page.nextCursor;
      setItems((prev) => [...prev, ...page.items]);
      setHasMore(page.has_more);
    } catch (err) {
      toast.error(apiErrorMessage(err) || loadMoreErrorMessage);
    } finally {
      setIsLoadingMore(false);
    }
  }, [enabled, fetchPage, hasMore, isLoadingMore, loadMoreErrorMessage]);

  const retry = useCallback(() => {
    cursorRef.current = initialCursor;
    setItems([]);
    setHasMore(false);
    void mutate();
  }, [initialCursor, mutate]);

  return {
    items,
    hasMore,
    isLoading: enabled ? isLoading && items.length === 0 : false,
    isLoadingMore,
    isError: error,
    loadMore,
    retry,
  };
}
